import { useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "@vnedyalk0v/react19-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import { countries, allProjects } from "../data/projects";
import { RotateCcw } from "lucide-react";

const GEO_URL = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

export default function WorldMap({ onSelectCountry }) {
  const [hover, setHover] = useState(null); // {name, iso, count, x, y}
  const [zoom, setZoom] = useState({ coordinates: [20, 15], zoom: 1 });
  const wrapRef = useRef(null);

  const countsByCountry = useMemo(() => {
    const m = {};
    for (const p of allProjects) m[p.country] = (m[p.country] || 0) + 1;
    return m;
  }, []);

  const activeCountryIso = new Set(Object.keys(countsByCountry));

  const nameToIso = {
    India: "IND", China: "CHN", Japan: "JPN", Malaysia: "MYS", Singapore: "SGP",
    Australia: "AUS", Indonesia: "IDN", Palau: "PLW",
    Nigeria: "NGA", Mali: "MLI", "South Africa": "ZAF", Namibia: "NAM",
    Mauritius: "MUS", Canada: "CAN",
    "United States of America": "USA", "United States": "USA",
  };

  const handleCountryClick = (iso) => {
    if (!activeCountryIso.has(iso)) return;
    const c = countries[iso];
    if (c) setZoom({ coordinates: c.coords, zoom: 3 });
    onSelectCountry?.(iso);
  };

  const reset = () => setZoom({ coordinates: [20, 15], zoom: 1 });

  // Clamp pan to reasonable world bounds — don't let the user scroll into the void.
  const handleMoveEnd = (p) => {
    const z = Math.max(1, Math.min(4.5, p.zoom));
    const [lng, lat] = p.coordinates;
    const clampedLng = Math.max(-170, Math.min(170, lng));
    const clampedLat = Math.max(-55, Math.min(75, lat));
    setZoom({ coordinates: [clampedLng, clampedLat], zoom: z });
  };

  return (
    <div
      ref={wrapRef}
      data-testid="world-map"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "var(--map-ocean)", border: "1px solid var(--border-soft)" }}
      onDoubleClick={reset}
    >
      <div className="absolute top-5 left-6 z-10 pointer-events-none">
        <div className="overline" style={{ color: "var(--accent-soft)" }}>Global Project Atlas</div>
        <div className="font-serif text-xl md:text-2xl mt-1" style={{ color: "var(--bg)" }}>
          {Object.keys(countsByCountry).length} countries · {allProjects.length} projects
        </div>
      </div>

      <button
        onClick={reset}
        data-testid="map-reset"
        className="absolute top-5 right-5 z-10 inline-flex items-center gap-2 text-xs px-3 py-2 transition-colors"
        style={{ backgroundColor: "rgba(244,243,240,0.08)", color: "var(--bg)", border: "1px solid rgba(244,243,240,0.18)" }}
      >
        <RotateCcw size={12} /> Reset view
      </button>

      {/* Legend */}
      <div className="absolute bottom-5 right-5 z-10 text-[10px] font-mono" style={{ color: "rgba(244,243,240,0.78)" }}>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: "var(--map-pin)" }} />
          <span>project country</span>
        </div>
        <div className="flex items-center gap-2 mt-1 opacity-70">
          <span>click to explore · double-click to reset</span>
        </div>
      </div>

      <ComposableMap
        projectionConfig={{ scale: 155 }}
        width={980}
        height={520}
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <ZoomableGroup
          center={zoom.coordinates}
          zoom={zoom.zoom}
          minZoom={1}
          maxZoom={4.5}
          translateExtent={[[-50, -50], [1030, 570]]}
          onMoveEnd={handleMoveEnd}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const iso = nameToIso[geo.properties.name];
                const isActive = iso && activeCountryIso.has(iso);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    className="map-geo"
                    data-testid={isActive ? `map-country-${iso}` : undefined}
                    onMouseEnter={(e) => {
                      if (!isActive) return setHover(null);
                      const rect = wrapRef.current?.getBoundingClientRect();
                      setHover({
                        name: geo.properties.name,
                        iso,
                        count: countsByCountry[iso] || 0,
                        x: e.clientX - (rect?.left || 0),
                        y: e.clientY - (rect?.top || 0),
                      });
                    }}
                    onMouseMove={(e) => {
                      if (!isActive) return;
                      const rect = wrapRef.current?.getBoundingClientRect();
                      setHover((h) => h && { ...h, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) });
                    }}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => handleCountryClick(iso)}
                    style={{
                      default: {
                        // Uniform highlight for all active countries — no density heat.
                        fill: isActive ? "#2E5A4F" : "var(--map-land)",
                        stroke: "rgba(244,243,240,0.14)",
                        strokeWidth: 0.45,
                        outline: "none",
                      },
                      hover: { fill: isActive ? "#4A8A7A" : "var(--map-land-hover)", outline: "none" },
                      pressed: { fill: "var(--accent)", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {Object.entries(countries).map(([iso, c]) => {
            const count = countsByCountry[iso] || 0;
            if (!count) return null;
            // Uniform, slightly larger pins so small countries (Palau, Singapore, Mauritius) are visible.
            const rScale = Math.max(zoom.zoom * 0.7, 1);
            return (
              <Marker key={iso} coordinates={c.coords} onClick={() => handleCountryClick(iso)}>
                <circle r={7 / rScale} fill="var(--map-pin)" className="pin-pulse" />
                <circle
                  r={4.5 / Math.max(zoom.zoom * 0.6, 1)}
                  fill="var(--map-pin)"
                  stroke="var(--bg)"
                  strokeWidth={0.8 / zoom.zoom}
                  className="pin"
                  data-testid={`map-pin-${iso}`}
                />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      <AnimatePresence>
        {hover && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute pointer-events-none z-20 px-3 py-1.5"
            style={{
              left: Math.min(hover.x + 14, (wrapRef.current?.clientWidth || 0) - 180),
              top: Math.max(hover.y - 44, 8),
              color: "var(--bg)",
              backgroundColor: "rgba(10,31,27,0.88)",
              border: "1px solid rgba(196,138,65,0.35)",
            }}
          >
            <div className="font-serif text-sm leading-tight">{hover.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--accent-soft)" }}>
              {hover.count} project{hover.count !== 1 ? "s" : ""}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
